import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import PdpSharedShippingInfoWrapper from '../PdpSharedShippingInfo/PdpSharedShippingInfoWrapper';
import { PdpSharedShippingHolidayDelivery } from '../PdpSharedShippingHolidayDelivery/PdpSharedShippingHolidayDelivery';
import { deliveryTitle } from '../messages/pdpDetailsMessages';

import { type PdpRespSidebarBlurbShipping_viewer$key } from './__generated__/PdpRespSidebarBlurbShipping_viewer.graphql';
import { type PdpRespSidebarBlurbShipping_item$key } from './__generated__/PdpRespSidebarBlurbShipping_item.graphql';
import { type PdpRespSidebarBlurbShipping_user$key } from './__generated__/PdpRespSidebarBlurbShipping_user.graphql';
import { type PdpRespSidebarBlurbShipping_sku$key } from './__generated__/PdpRespSidebarBlurbShipping_sku.graphql';

// styles
import styles from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb.scss';

const viewerFragment = graphql`
    fragment PdpRespSidebarBlurbShipping_viewer on Viewer {
        pdpHolidayShippingFeatureFlag: isEnabledByLocale(featureName: "HOLIDAY_SHIPPING")
        ...PdpSharedShippingHolidayDelivery_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespSidebarBlurbShipping_user on User {
        ...PdpSharedShippingInfoWrapper_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespSidebarBlurbShipping_item on Item {
        ...PdpSharedShippingInfoWrapper_item
        ...PdpSharedShippingHolidayDelivery_item
        attributes {
            leadTime {
                option {
                    urlLabel
                }
            }
        }
    }
`;

const skuFramgent = graphql`
    fragment PdpRespSidebarBlurbShipping_sku on ItemSkuType {
        ...PdpSharedShippingHolidayDelivery_sku
        ...PdpSharedShippingInfoWrapper_sku
    }
`;

export const PdpRespSidebarBlurbShipping: FC<{
    item: PdpRespSidebarBlurbShipping_item$key;
    viewer: PdpRespSidebarBlurbShipping_viewer$key;
    user: PdpRespSidebarBlurbShipping_user$key | null | undefined;
    sku: PdpRespSidebarBlurbShipping_sku$key | null | undefined;
    currency: string;
    renderTooltip?: boolean;
    isClient: boolean;
    itemQuantity?: number | null;
    shortLinkCta?: boolean;
}> = ({
    item: itemRef,
    viewer: viewerRef,
    user: userRef,
    sku: skuRef,
    currency,
    renderTooltip,
    isClient,
    itemQuantity,
    shortLinkCta,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const sku = useFragment(skuFramgent, skuRef);

    const { pdpHolidayShippingFeatureFlag } = viewer;
    const leadTime = item.attributes?.leadTime?.[0]?.option?.urlLabel;

    return (
        <>
            <PdpSharedBlurb IconComponent={null} zeroPadding iconClass={styles.printHide}>
                <PdpSharedShippingInfoWrapper
                    sku={sku}
                    item={item}
                    currency={currency}
                    leadTime={leadTime}
                    renderTooltip={renderTooltip}
                    user={user}
                    isClient={isClient}
                    title={<span className={styles.blurbTitle}>{deliveryTitle}</span>}
                    displayInline
                    itemQuantity={itemQuantity}
                    positioning="sidebar"
                    shortLinkCta={shortLinkCta}
                />
            </PdpSharedBlurb>
            {pdpHolidayShippingFeatureFlag && (
                <PdpSharedShippingHolidayDelivery
                    sku={sku}
                    item={item}
                    viewer={viewer}
                    useTooltipDetails
                    isClient={isClient}
                />
            )}
        </>
    );
};
